import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "wouter";
import { AlertCircle, CheckCircle2, Circle, CloudUpload, Download, FileText, Loader2, Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { YearFilter } from "@/components/filters/YearFilter";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/http";
import { useHydrateYearFromStorage, useYear } from "@/state/yearStore";
import { adviceForImportAnalyseError, filenameLooksLikePackBooking, PACK_BOOKING_WIZARD_HINT } from "@shared/campaignImportHints";

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

type CampaignTile = {
  campaignName: string;
  declaredStoreCount: number | null;
  detectedStoreCount: number;
  formatCount: number;
  formats: string[];
  installerResolvedCount: number;
  installerUnresolvedCount: number;
  statuses: Array<"ready" | "needs_format" | "installer_issues" | "warnings">;
};

type UnresolvedOperatorItem = {
  storeNo?: string;
  storeName?: string;
  formatName?: string;
  /** Operators who already have at least one format assigned in this store */
  activeOperators?: Array<{ id: number; name: string }>;
  // Legacy shape fallback
  raw?: string;
  count: number;
};

type ImportRow = {
  campaignName: string;
  storeNo: string;
  storeName: string;
  storeId: number | null;
  formatName: string;
  mediaFormatId: number | null;
  quantity: number;
  // Point-in-time operator id captured at analysis time. Used to build the
  // campaign group's operatorAssignments snapshot so snapshot rebuilds are
  // not affected by later SFO table mutations.
  installerResolvedId?: number | null;
};

type AnalysisResponse = {
  campaigns: CampaignTile[];
  rows: ImportRow[];
  detectedFormats: Array<{
    importFormatName: string;
    normalizedName: string;
    matchedMediaFormatId: number | null;
    matchedMediaFormatName: string | null;
    status: "matched" | "mapped" | "unmatched";
    storesUsingFormat: number;
    storesWithFormatAvailable: number;
  }>;
  validation: {
    blocking: string[];
    warnings: string[];
  };
  installers: {
    resolved: Array<{ raw: string; resolved: string }>;
    unresolved: Array<UnresolvedOperatorItem>;
  };
  counts: {
    campaignsDetected: number;
    rowsNormalized: number;
    uniqueStores: number;
  };
};

type ReconciliationResponse = {
  snapshotLabel: string;
  matchedRows: number;
  mismatchRows: number;
  blocking: boolean;
  mismatches: Array<{
    type: "missing_in_import" | "unexpected_in_import" | "quantity_mismatch";
    campaignName: string;
    formatName: string;
    storeName: string;
    expected: number;
    actual: number;
  }>;
};

type MediaFormatOption = {
  id: number;
  name: string;
};

type OperatorOption = {
  id: number;
  name: string;
};

type OperatorTaskTemplateOption = {
  id: number;
  name: string;
  taskName?: string;
};

type RetailerOption = {
  id: number;
  name: string;
};

type CycleOption = {
  id: number;
  name: string;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
};

type FormatDecision = {
  action: "unresolved" | "matched" | "use_existing" | "create_new";
  mediaFormatId: number | null;
  /** True when the mediaFormatId was auto-populated by fuzzy match and hasn't been confirmed by the user */
  isSuggested?: boolean;
};

const STEP_LABELS: Array<{ id: WizardStep; label: string }> = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Detected Campaigns" },
  { id: 3, label: "Formats & Stores" },
  { id: 4, label: "Operator Coverage" },
  { id: 5, label: "Validation" },
  { id: 6, label: "Kitting Reconciliation (Optional)" },
  { id: 7, label: "Preview" },
];

type WizardPillTone = "neutral" | "active" | "danger" | "warning" | "success" | "info";

const WIZARD_PILL_STYLES: Record<WizardPillTone, CSSProperties> = {
  neutral: { backgroundColor: "#f3f4f6", color: "#111827", borderColor: "#d1d5db" },
  active: { backgroundColor: "#1d4ed8", color: "#ffffff", borderColor: "#1e40af" },
  danger: { backgroundColor: "#b91c1c", color: "#ffffff", borderColor: "#991b1b" },
  warning: { backgroundColor: "#b45309", color: "#ffffff", borderColor: "#92400e" },
  success: { backgroundColor: "#166534", color: "#ffffff", borderColor: "#14532d" },
  info: { backgroundColor: "#0f766e", color: "#ffffff", borderColor: "#115e59" },
};

function formatCycleDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" });
}

function getCycleDateRangeLabel(cycle: CycleOption): string | null {
  const start = formatCycleDate(cycle.startDate);
  const end = formatCycleDate(cycle.endDate);
  if (start && end) return `${start} - ${end}`;
  return start ?? end;
}

function getCycleOptionLabel(cycle: CycleOption): string {
  const parts = [cycle.name];
  const dateRange = getCycleDateRangeLabel(cycle);
  if (dateRange) parts.push(dateRange);
  if (cycle.status) parts.push(cycle.status);
  return parts.join(" - ");
}

function WizardPill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: WizardPillTone;
}) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
      style={WIZARD_PILL_STYLES[tone]}
    >
      {children}
    </span>
  );
}

export default function CampaignUpload() {
  const { toast } = useToast();
  const importInputRef = useRef<HTMLInputElement>(null);
  const kittingInputRef = useRef<HTMLInputElement>(null);
  const cycleLoadRequestRef = useRef(0);
  const analysisRequestRef = useRef(0);
  const reconciliationRequestRef = useRef(0);
  useHydrateYearFromStorage();
  const selectedYear = useYear();

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [kittingFile, setKittingFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [reconciliation, setReconciliation] = useState<ReconciliationResponse | null>(null);
  const [mediaFormats, setMediaFormats] = useState<MediaFormatOption[]>([]);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [operatorTaskTemplates, setOperatorTaskTemplates] = useState<OperatorTaskTemplateOption[]>([]);
  const [campaignTaskSelections, setCampaignTaskSelections] = useState<Record<string, number | null>>({});
  const [isCreatingCampaigns, setIsCreatingCampaigns] = useState(false);
  const [createdCampaigns, setCreatedCampaigns] = useState<Array<{ id: number; name: string }>>([]);
  // Per-campaign progress ticker for the bulk import create step. The
  // list is seeded when the user clicks "Create Campaigns" and entries
  // are flipped through pending -> creating -> success/failed as each
  // campaign is POSTed sequentially.
  const [campaignCreationStatus, setCampaignCreationStatus] = useState<
    Array<{
      name: string;
      status: "pending" | "creating" | "success" | "failed";
      id?: number;
      error?: string;
    }>
  >([]);
  const [retailers, setRetailers] = useState<RetailerOption[]>([]);
  const [cycles, setCycles] = useState<CycleOption[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [isCyclesLoading, setIsCyclesLoading] = useState(false);
  const [formatDecisions, setFormatDecisions] = useState<Record<string, FormatDecision>>({});
  const [formatMappingsToOnboard, setFormatMappingsToOnboard] = useState<Record<string, number>>({});
  const [operatorOverrides, setOperatorOverrides] = useState<Record<string, number | null>>({});
  const [step4Acknowledged, setStep4Acknowledged] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [isPersistingFormats, setIsPersistingFormats] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);

  const resetImportRunState = () => {
    analysisRequestRef.current += 1;
    reconciliationRequestRef.current += 1;
    setAnalysis(null);
    setReconciliation(null);
    setFormatDecisions({});
    setFormatMappingsToOnboard({});
    setOperatorOverrides({});
    setStep4Acknowledged(false);
    setCampaignTaskSelections({});
    setCreatedCampaigns([]);
    setCampaignCreationStatus([]);
    setKittingFile(null);
    setIsAnalysing(false);
    setIsPersistingFormats(false);
    setIsReconciling(false);
    setCurrentStep(1);
  };

  const stepProgress = useMemo(() => (currentStep / STEP_LABELS.length) * 100, [currentStep]);

  // Dice bigram similarity (0–1). Case-insensitive, punctuation-normalised.
  const digramScore = (a: string, b: string): number => {
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
    const na = norm(a);
    const nb = norm(b);
    if (na === nb) return 1;
    if (na.includes(nb) || nb.includes(na)) return 0.9;
    const bigrams = (s: string): string[] =>
      Array.from({ length: Math.max(0, s.length - 1) }, (_, i) => s.slice(i, i + 2));
    const ba = bigrams(na);
    const bb = new Set(bigrams(nb));
    const shared = ba.filter((g) => bb.has(g)).length;
    return (2 * shared) / (ba.length + bb.size || 1);
  };

  const bestFuzzyMatch = (importName: string): { id: number; name: string; score: number } | null => {
    let best: { id: number; name: string; score: number } | null = null;
    for (const mf of mediaFormats) {
      const score = digramScore(importName, mf.name);
      if (!best || score > best.score) best = { id: mf.id, name: mf.name, score };
    }
    return best && best.score >= 0.35 ? best : null;
  };

  // Auto-populate fuzzy suggestions for unresolved formats once mediaFormats are available.
  useEffect(() => {
    if (!analysis || mediaFormats.length === 0) return;
    setFormatDecisions((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const format of analysis.detectedFormats ?? []) {
        const current = next[format.importFormatName];
        if (!current || current.action === "unresolved") {
          const match = bestFuzzyMatch(format.importFormatName);
          if (match) {
            next[format.importFormatName] = { action: "use_existing", mediaFormatId: match.id, isSuggested: true };
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, mediaFormats]);

  const blockingErrors = analysis?.validation.blocking ?? [];
  const unresolvedInstallers = analysis?.installers.unresolved ?? [];
  const hasFormatGap = (analysis?.campaigns ?? []).some((c) => c.formatCount === 0);
  const normaliseUnresolved = (item: UnresolvedOperatorItem) => {
    if (item.storeNo || item.storeName || item.formatName) {
      return {
        storeNo: item.storeNo ?? "",
        storeName: item.storeName ?? item.storeNo ?? "",
        formatName: item.formatName ?? "",
      };
    }
    // Legacy: raw = "200 | Floor graphic"
    const parts = (item.raw ?? "").split("|").map((p) => p.trim());
    return {
      storeNo: parts[0] ?? "",
      storeName: parts[0] ?? item.raw ?? "",
      formatName: parts.slice(1).join(" | ").trim(),
    };
  };

  const hasUnresolvedFormatDecision = useMemo(() => {
    if (!analysis) return true;
    return (analysis.detectedFormats ?? []).some((format) => {
      const decision = formatDecisions[format.importFormatName];
      if (!decision || decision.action === "unresolved") return true;
      if (decision.action === "use_existing") return !decision.mediaFormatId;
      return false; // "matched", "create_new", or use_existing with id — all good
    });
  }, [analysis, formatDecisions]);

  const hasSuggestedFormatDecisions = useMemo(() => {
    return Object.values(formatDecisions).some((d) => d.isSuggested);
  }, [formatDecisions]);

  const hasCreateNewFormatDecisions = useMemo(() => {
    return Object.values(formatDecisions).some((d) => d.action === "create_new");
  }, [formatDecisions]);

  const allOperatorsAssigned = useMemo(() => {
    if (unresolvedInstallers.length === 0) return true;
    return unresolvedInstallers.every((item) => {
      const { storeNo, formatName } = normaliseUnresolved(item);
      const key = `${storeNo}|||${formatName}`;
      return (operatorOverrides[key] ?? null) !== null;
    });
  // normaliseUnresolved is a stable function defined in the component, OK to omit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedInstallers, operatorOverrides]);

  const isOperatorCoverageWarning = (message: unknown) => {
    const text = String(message ?? "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    return (
      text.includes("no operator assigned") ||
      text.includes("have no operator assigned") ||
      text.includes("operator assigned") ||
      (text.includes("operator") && text.includes("assign"))
    );
  };

  const validationWarnings = useMemo(() => {
    const warnings = analysis?.validation.warnings ?? [];
    // Step 5 must not show stale backend operator-coverage warnings.
    return warnings.filter((message) => !isOperatorCoverageWarning(message));
  }, [analysis]);

  const canProceedFromStep = (step: WizardStep) => {
    if (step === 1) return Boolean(selectedRetailerId && selectedCycleId && importFile && analysis);
    if (step === 2) return Boolean(analysis && analysis.campaigns.length > 0);
    if (step === 3) return Boolean(analysis && !hasFormatGap && !hasUnresolvedFormatDecision);
    if (step === 4) return unresolvedInstallers.length === 0 || allOperatorsAssigned || step4Acknowledged;
    if (step === 5) return blockingErrors.length === 0;
    if (step === 6) return true;
    return true;
  };

  const getStepBlockReason = (step: WizardStep): string | null => {
    if (step === 1) {
      if (!selectedRetailerId) return "Select a retailer to start import analysis.";
      if (!selectedCycleId) return "Select a cycle before uploading the import file.";
      if (!importFile) return "Upload the import file to continue.";
      if (!analysis) return "Run analysis for the uploaded file to continue.";
      return null;
    }
    if (step === 3) {
      if (hasFormatGap) return "At least one detected campaign has no formats.";
      if (hasUnresolvedFormatDecision) return "Resolve all red 'Needs action' formats before continuing.";
      return null;
    }
    if (step === 4) {
      if (unresolvedInstallers.length > 0 && !allOperatorsAssigned && !step4Acknowledged) {
        return "Assign operators for unresolved rows, or acknowledge and proceed.";
      }
      return null;
    }
    if (step === 5) {
      if (blockingErrors.length > 0) return blockingErrors[0];
      return null;
    }
    if (step === 6) return null;
    return null;
  };

  const currentStepBlockReason = getStepBlockReason(currentStep);
  const isStepTransitioning = isAnalysing || isPersistingFormats;
  const selectedRetailerName = retailers.find((retailer) => retailer.id === selectedRetailerId)?.name ?? "selected retailer";
  const selectedCycleName = cycles.find((cycle) => cycle.id === selectedCycleId)?.name ?? "selected cycle";
  const selectedCampaignsUrl =
    selectedRetailerId && selectedCycleId
      ? `/campaigns?retailerId=${selectedRetailerId}&cycleId=${selectedCycleId}&year=${selectedYear}`
      : "/campaigns";

  const normaliseKey = (campaign: string, format: string, store: string) =>
    `${campaign.trim().toLowerCase()}|${format.trim().toLowerCase()}|${store.trim().toLowerCase()}`;

  const previewMatrix = useMemo(() => {
    const rows = analysis?.rows ?? [];
    const campaignFormats = new Map<string, string[]>();
    const stores = new Map<string, { storeNo: string; storeName: string }>();
    const uploadedByKey = new Map<string, number>();

    for (const row of rows) {
      if (!campaignFormats.has(row.campaignName)) campaignFormats.set(row.campaignName, []);
      const formats = campaignFormats.get(row.campaignName)!;
      if (!formats.includes(row.formatName)) formats.push(row.formatName);

      const storeKey = `${row.storeNo}|||${row.storeName}`;
      if (!stores.has(storeKey)) stores.set(storeKey, { storeNo: row.storeNo, storeName: row.storeName });

      const key = normaliseKey(row.campaignName, row.formatName, row.storeName);
      uploadedByKey.set(key, (uploadedByKey.get(key) ?? 0) + (row.quantity ?? 0));
    }

    const generatedByKey = new Map(uploadedByKey);
    for (const mismatch of reconciliation?.mismatches ?? []) {
      const key = normaliseKey(mismatch.campaignName, mismatch.formatName, mismatch.storeName);
      generatedByKey.set(key, mismatch.expected);
      uploadedByKey.set(key, mismatch.actual);
    }

    return {
      campaignFormats: Array.from(campaignFormats.entries()).map(([campaignName, formats]) => ({
        campaignName,
        formats,
      })),
      stores: Array.from(stores.values()).sort((a, b) =>
        `${a.storeName} ${a.storeNo}`.localeCompare(`${b.storeName} ${b.storeNo}`),
      ),
      uploadedByKey,
      generatedByKey,
    };
  }, [analysis?.rows, reconciliation]);

  const exportPreviewCsv = () => {
    const lines: string[] = [];
    lines.push(["Store No", "Store Name", "Campaign", "Format", "Uploaded Qty", "Generated Qty"].join(","));

    for (const store of previewMatrix.stores) {
      for (const campaign of previewMatrix.campaignFormats) {
        for (const formatName of campaign.formats) {
          const key = normaliseKey(campaign.campaignName, formatName, store.storeName);
          const uploadedQty = previewMatrix.uploadedByKey.get(key) ?? 0;
          const generatedQty = previewMatrix.generatedByKey.get(key) ?? uploadedQty;
          if (uploadedQty === 0 && generatedQty === 0) continue;
          const esc = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;
          lines.push(
            [
              esc(store.storeNo),
              esc(store.storeName),
              esc(campaign.campaignName),
              esc(formatName),
              esc(uploadedQty),
              esc(generatedQty),
            ].join(","),
          );
        }
      }
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `import-preview-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const campaignCreationPlan = useMemo(() => {
    type StorePlan = {
      storeId: number;
      storeNo: string;
      storeName: string;
      formats: Map<number, number>;
    };
    type CampaignPlan = {
      campaignName: string;
      stores: StorePlan[];
      missingStoreRows: number;
      missingFormatRows: number;
      totalRows: number;
    };

    const byCampaign = new Map<string, Map<number, StorePlan>>();
    const missingByCampaign = new Map<string, { missingStoreRows: number; missingFormatRows: number; totalRows: number }>();

    for (const row of analysis?.rows ?? []) {
      const qty = Number(row.quantity ?? 0);
      if (!Number.isFinite(qty) || qty <= 0) continue;

      if (!missingByCampaign.has(row.campaignName)) {
        missingByCampaign.set(row.campaignName, { missingStoreRows: 0, missingFormatRows: 0, totalRows: 0 });
      }
      const missing = missingByCampaign.get(row.campaignName)!;
      missing.totalRows += 1;

      if (!row.storeId) {
        missing.missingStoreRows += 1;
        continue;
      }
      if (!row.mediaFormatId) {
        missing.missingFormatRows += 1;
        continue;
      }

      if (!byCampaign.has(row.campaignName)) byCampaign.set(row.campaignName, new Map());
      const byStore = byCampaign.get(row.campaignName)!;
      if (!byStore.has(row.storeId)) {
        byStore.set(row.storeId, {
          storeId: row.storeId,
          storeNo: row.storeNo,
          storeName: row.storeName,
          formats: new Map(),
        });
      }
      const storePlan = byStore.get(row.storeId)!;
      storePlan.formats.set(row.mediaFormatId, (storePlan.formats.get(row.mediaFormatId) ?? 0) + qty);
    }

    const orderedCampaigns = (analysis?.campaigns ?? []).map((c) => c.campaignName);
    return orderedCampaigns.map((campaignName): CampaignPlan => {
      const byStore = byCampaign.get(campaignName) ?? new Map<number, StorePlan>();
      const missing = missingByCampaign.get(campaignName) ?? { missingStoreRows: 0, missingFormatRows: 0, totalRows: 0 };
      return {
        campaignName,
        stores: Array.from(byStore.values()).sort((a, b) =>
          `${a.storeName} ${a.storeNo}`.localeCompare(`${b.storeName} ${b.storeNo}`),
        ),
        missingStoreRows: missing.missingStoreRows,
        missingFormatRows: missing.missingFormatRows,
        totalRows: missing.totalRows,
      };
    });
  }, [analysis?.rows, analysis?.campaigns]);

  const campaignPreviewCards = useMemo(() => {
    const rows = analysis?.rows ?? [];
    const byCampaign = new Map<string, { formats: Set<string>; stores: Set<string> }>();
    for (const row of rows) {
      if (!byCampaign.has(row.campaignName)) {
        byCampaign.set(row.campaignName, { formats: new Set(), stores: new Set() });
      }
      const bucket = byCampaign.get(row.campaignName)!;
      if (row.quantity > 0) {
        bucket.formats.add(row.formatName);
        bucket.stores.add(`${row.storeName} (#${row.storeNo})`);
      }
    }

    return campaignCreationPlan.map((plan) => {
      const details = byCampaign.get(plan.campaignName) ?? { formats: new Set<string>(), stores: new Set<string>() };
      return {
        campaignName: plan.campaignName,
        groupCount: plan.stores.length,
        formatNames: Array.from(details.formats).sort((a, b) => a.localeCompare(b)),
        storeNames: Array.from(details.stores).sort((a, b) => a.localeCompare(b)),
        missingStoreRows: plan.missingStoreRows,
        missingFormatRows: plan.missingFormatRows,
      };
    });
  }, [analysis?.rows, campaignCreationPlan]);

  useEffect(() => {
    if (!analysis?.campaigns?.length) return;
    const standardPostingTask =
      operatorTaskTemplates.find((t) => t.name.trim().toLowerCase() === "standard posting") ??
      operatorTaskTemplates.find((t) => t.name.trim().toLowerCase().includes("standard posting")) ??
      operatorTaskTemplates[0];
    const defaultTaskId = standardPostingTask?.id ?? null;

    setCampaignTaskSelections((prev) => {
      if (!defaultTaskId) return prev;
      const next = { ...prev };
      let changed = false;
      for (const campaign of analysis.campaigns) {
        if (next[campaign.campaignName] == null) {
          next[campaign.campaignName] = defaultTaskId;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [analysis?.campaigns, operatorTaskTemplates]);

  useEffect(() => {
    if (unresolvedInstallers.length === 0) return;
    setOperatorOverrides((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const item of unresolvedInstallers) {
        const { storeNo, formatName } = normaliseUnresolved(item);
        const key = `${storeNo}|||${formatName}`;
        if (key in next) continue;

        const sortedStoreOperators =
          item.activeOperators && item.activeOperators.length > 0
            ? [...item.activeOperators].sort((a, b) => a.name.localeCompare(b.name))
            : [];
        const autoSelected = sortedStoreOperators[0] ?? operators[0] ?? null;
        next[key] = autoSelected ? autoSelected.id : null;
        changed = true;
      }

      return changed ? next : prev;
    });
    // normaliseUnresolved is stable in this component lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedInstallers, operators]);

  const createImportedCampaigns = async () => {
    if (!selectedRetailerId || !selectedCycleId) {
      toast({
        title: "Missing scope",
        description: "Select retailer and cycle before creating campaigns.",
        variant: "destructive",
      });
      return;
    }
    if (!campaignCreationPlan.length) {
      toast({
        title: "Nothing to create",
        description: "No campaign groups were found in the import.",
        variant: "destructive",
      });
      return;
    }

    const campaignsWithoutTask = campaignCreationPlan
      .filter((plan) => !campaignTaskSelections[plan.campaignName])
      .map((plan) => plan.campaignName);
    if (campaignsWithoutTask.length > 0) {
      toast({
        title: "Select operator task per campaign",
        description: `Missing operator task for: ${campaignsWithoutTask.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    const unresolvedCampaigns = campaignCreationPlan
      .filter((plan) => plan.missingStoreRows > 0 || plan.missingFormatRows > 0)
      .map((plan) => `${plan.campaignName} (store gaps: ${plan.missingStoreRows}, format gaps: ${plan.missingFormatRows})`);
    if (unresolvedCampaigns.length > 0) {
      toast({
        title: "Cannot create yet",
        description: `Resolve mapping gaps first: ${unresolvedCampaigns.join("; ")}`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreatingCampaigns(true);
      setCreatedCampaigns([]);
      // Seed the live progress list so the UI can tick through pending -> creating -> done.
      setCampaignCreationStatus(
        campaignCreationPlan.map((plan) => ({
          name: plan.campaignName,
          status: "pending",
        })),
      );

      const created: Array<{ id: number; name: string }> = [];
      const failures: Array<{ name: string; error: string }> = [];
      const formatNameById = new Map<number, string>();
      for (const row of analysis?.rows ?? []) {
        if (Number.isInteger(row.mediaFormatId) && row.mediaFormatId && !formatNameById.has(row.mediaFormatId)) {
          formatNameById.set(row.mediaFormatId, row.formatName);
        }
      }

      const resolvedOperatorIdByStoreFormat = new Map<string, number>();
      for (const row of analysis?.rows ?? []) {
        if (!row.storeId || !row.mediaFormatId) continue;
        if (typeof row.installerResolvedId === "number" && row.installerResolvedId > 0) {
          resolvedOperatorIdByStoreFormat.set(
            `${row.storeId}|${row.mediaFormatId}`,
            row.installerResolvedId,
          );
        }
      }

      const formatIdsToOnboard = new Set(
        Object.values(formatMappingsToOnboard)
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0),
      );

      if (formatIdsToOnboard.size > 0) {
        const requirementByPair = new Map<string, { storeId: number; mediaFormatId: number; quantity: number }>();
        const assignmentByPair = new Map<string, { storeId: number; mediaFormatId: number; operatorId: number }>();
        const missingOperatorLabels: string[] = [];

        for (const plan of campaignCreationPlan) {
          for (const store of plan.stores) {
            for (const [mediaFormatId, quantity] of store.formats.entries()) {
              if (!formatIdsToOnboard.has(mediaFormatId)) continue;

              const pairKey = `${store.storeId}|${mediaFormatId}`;
              const existingRequirement = requirementByPair.get(pairKey);
              requirementByPair.set(pairKey, {
                storeId: store.storeId,
                mediaFormatId,
                quantity: Math.max(existingRequirement?.quantity ?? 0, quantity),
              });

              const formatName = formatNameById.get(mediaFormatId) ?? "";
              const overrideKey = `${store.storeNo}|||${formatName}`;
              const resolvedKey = `${store.storeId}|${mediaFormatId}`;
              const operatorId = Object.prototype.hasOwnProperty.call(operatorOverrides, overrideKey)
                ? (operatorOverrides[overrideKey] ?? 0)
                : (resolvedOperatorIdByStoreFormat.get(resolvedKey) ?? 0);

              if (operatorId > 0) {
                assignmentByPair.set(pairKey, { storeId: store.storeId, mediaFormatId, operatorId });
              } else {
                missingOperatorLabels.push(`${store.storeName} / ${formatName || `Format ${mediaFormatId}`}`);
              }
            }
          }
        }

        if (missingOperatorLabels.length > 0) {
          throw new Error(
            `Assign operators for new format rows before creating campaigns: ${missingOperatorLabels.slice(0, 5).join(", ")}${missingOperatorLabels.length > 5 ? "..." : ""}`,
          );
        }

        const requirements = Array.from(requirementByPair.values());
        const assignments = Array.from(assignmentByPair.values());

        if (requirements.length > 0) {
          const response = await apiFetch("/api/store-media-requirements/bulk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ requirements }),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload?.success === false || payload?.summary?.failed > 0) {
            throw new Error(payload?.error || "Failed to create store/format rows for new formats.");
          }
        }

        if (assignments.length > 0) {
          const response = await apiFetch("/api/store-format-operator-assignments/bulk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ assignments }),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload?.success === false || payload?.summary?.failed > 0) {
            throw new Error(payload?.error || "Failed to create store/operator rows for new formats.");
          }
        }

        toast({
          title: "New formats onboarded",
          description: `Created store/operator setup for ${formatIdsToOnboard.size} new format(s) before campaign creation.`,
        });
      }

      for (let planIdx = 0; planIdx < campaignCreationPlan.length; planIdx += 1) {
        const plan = campaignCreationPlan[planIdx];
        const operatorTaskId = campaignTaskSelections[plan.campaignName]!;

        // Flip this row to "creating" BEFORE the fetch so the live list updates
        // mid-loop. We yield a microtask so React commits and the browser paints
        // before we block on the server round-trip.
        setCampaignCreationStatus((prev) =>
          prev.map((entry, i) =>
            i === planIdx ? { ...entry, status: "creating" } : entry,
          ),
        );
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        const groupsByFormatQuantity = new Map<
          string,
          {
            storeIds: number[];
            formatEntries: Array<{ mediaFormatId: number; quantity: number }>;
            operatorAssignments: Array<{
              storeId: number;
              mediaFormatId: number;
              operatorId: number;
            }>;
          }
        >();

        // A group has one quantity per format, so imports with variable
        // quantities must be split into groups of stores that share the same
        // format/quantity pattern. This prevents campaign-total quantities
        // from being applied to every store in the campaign.
        for (const store of plan.stores) {
          const formatEntries = Array.from(store.formats.entries())
            .map(([mediaFormatId, quantity]) => ({ mediaFormatId, quantity }))
            .sort((a, b) => a.mediaFormatId - b.mediaFormatId);
          const signature = formatEntries
            .map((entry) => `${entry.mediaFormatId}:${entry.quantity}`)
            .join("|");

          if (!groupsByFormatQuantity.has(signature)) {
            groupsByFormatQuantity.set(signature, {
              storeIds: [],
              formatEntries,
              operatorAssignments: [],
            });
          }

          const group = groupsByFormatQuantity.get(signature)!;
          group.storeIds.push(store.storeId);

          for (const { mediaFormatId } of formatEntries) {
            const formatName = formatNameById.get(mediaFormatId) ?? "";
            const overrideKey = `${store.storeNo}|||${formatName}`;
            const resolvedKey = `${store.storeId}|${mediaFormatId}`;

            let operatorId: number;
            if (resolvedOperatorIdByStoreFormat.has(resolvedKey)) {
              // The live store/format -> operator assignment is authoritative.
              // It must win over any step-4 override: overrides are only ever
              // offered for store/format combos that were UNRESOLVED, so once a
              // combo resolves (e.g. after the format is mapped in step 3) any
              // lingering override is a stale auto-pick from when the format was
              // still unmatched and must not shadow the resolved operator.
              operatorId = resolvedOperatorIdByStoreFormat.get(resolvedKey)!;
            } else if (Object.prototype.hasOwnProperty.call(operatorOverrides, overrideKey)) {
              // Still unresolved: fall back to the operator the user picked (or
              // cleared) in step 4. null/undefined/0 all map to "intentionally
              // unassigned".
              operatorId = operatorOverrides[overrideKey] ?? 0;
            } else {
              operatorId = 0;
            }

            group.operatorAssignments.push({
              storeId: store.storeId,
              mediaFormatId,
              operatorId,
            });
          }
        }

        const campaignSlug = plan.campaignName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
        const groupedImportEntries = Array.from(groupsByFormatQuantity.values());
        const groups = groupedImportEntries.map((group, groupIdx) => ({
          groupId: `import-${campaignSlug}-${groupIdx + 1}`,
          groupName: groupedImportEntries.length === 1 ? plan.campaignName : `${plan.campaignName} - Set ${groupIdx + 1}`,
          storeIds: group.storeIds,
          operatorTaskId,
          formatEntries: group.formatEntries,
          operatorAssignments: group.operatorAssignments,
        }));

        const payload = {
          campaign: {
            name: plan.campaignName,
            description: `Imported from ${importFile?.name ?? "campaign import"}`,
            cycleId: selectedCycleId,
            statusId: 1,
            campaignTypeKey: "Standard",
            createRemoval: false,
          },
          retailers: [selectedRetailerId],
          stores: [],
          packs: [],
          media: [],
          groups,
          importMetadata: {
            source: "campaign-import-wizard",
            quantityMode: "per-store",
          },
        };

        try {
          const response = await apiFetch("/api/campaigns/with-relationships", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok || !body?.id) {
            throw new Error(
              body?.details ||
                body?.error ||
                `Failed creating campaign '${plan.campaignName}'`,
            );
          }
          const newId = body.id as number;
          const newName = (body.name as string) ?? plan.campaignName;
          created.push({ id: newId, name: newName });
          setCampaignCreationStatus((prev) =>
            prev.map((entry, i) =>
              i === planIdx ? { ...entry, status: "success", id: newId } : entry,
            ),
          );
        } catch (rowError: any) {
          const message =
            rowError?.message || `Unknown error creating '${plan.campaignName}'`;
          failures.push({ name: plan.campaignName, error: message });
          setCampaignCreationStatus((prev) =>
            prev.map((entry, i) =>
              i === planIdx
                ? { ...entry, status: "failed", error: message }
                : entry,
            ),
          );
          // Best-effort bulk import: log the failure and keep going so an
          // error late in the batch doesn't block earlier successes.
        }
      }

      setCreatedCampaigns(created);

      if (failures.length === 0) {
        toast({
          title: "Campaigns created",
          description: `Created ${created.length} campaign(s) with operator tasks.`,
        });
      } else if (created.length === 0) {
        toast({
          title: "Creation failed",
          description: `All ${failures.length} campaign(s) failed. See list below.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Partial import",
          description: `Created ${created.length} of ${campaignCreationPlan.length}. ${failures.length} failed — see list below.`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      // Safety net for unexpected errors outside the per-row try/catch.
      toast({
        title: "Creation failed",
        description: error?.message || "Could not create imported campaigns.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingCampaigns(false);
    }
  };

  const loadRetailers = async () => {
    if (retailers.length > 0) return;
    const response = await apiFetch("/api/retailers");
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
    setRetailers(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({ id: item.id as number, name: item.name as string }))
        .sort((a: RetailerOption, b: RetailerOption) => a.name.localeCompare(b.name)),
    );
  };

  const loadCyclesForRetailer = async (retailerId: number, year: number) => {
    const requestId = cycleLoadRequestRef.current + 1;
    cycleLoadRequestRef.current = requestId;
    setIsCyclesLoading(true);
    setCycles([]);
    setSelectedCycleId(null);
    try {
      const response = await apiFetch(`/api/booking-cycles?retailerId=${retailerId}&year=${year}`);
      if (requestId !== cycleLoadRequestRef.current) return;
      if (!response.ok) return;
      const payload = await response.json();
      const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
      setCycles(
        items
          .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
          .map((item: any) => ({
            id: item.id as number,
            name: item.name as string,
            status: item.status,
            startDate: item.startDate ?? null,
            endDate: item.endDate ?? null,
          }))
          .sort((a: CycleOption, b: CycleOption) => b.id - a.id),
      );
    } finally {
      if (requestId === cycleLoadRequestRef.current) {
        setIsCyclesLoading(false);
      }
    }
  };

  const loadMediaFormats = async () => {
    const url = selectedRetailerId
      ? `/api/media-formats?retailerId=${selectedRetailerId}`
      : "/api/media-formats";
    const response = await apiFetch(url);
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
    setMediaFormats(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({ id: item.id as number, name: item.name as string })),
    );
  };

  const loadOperators = async () => {
    if (operators.length > 0) return;
    const response = await apiFetch("/api/operators");
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
    setOperators(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({ id: item.id as number, name: item.name as string }))
        .sort((a: OperatorOption, b: OperatorOption) => a.name.localeCompare(b.name)),
    );
  };

  const loadOperatorTaskTemplates = async () => {
    if (operatorTaskTemplates.length > 0) return;
    const response = await apiFetch("/api/operator-tasks");
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : [];
    setOperatorTaskTemplates(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({
          id: item.id as number,
          name: item.name as string,
          taskName: typeof item.task_name === "string" ? (item.task_name as string) : undefined,
        }))
        .sort((a: OperatorTaskTemplateOption, b: OperatorTaskTemplateOption) => a.name.localeCompare(b.name)),
    );
  };

  // Load retailers on mount, load formats when reaching step 3, operators at step 4.
  useEffect(() => {
    void loadRetailers();
  }, []);

  useEffect(() => {
    if (!selectedRetailerId) return;
    resetImportRunState();
    void loadCyclesForRetailer(selectedRetailerId, selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRetailerId, selectedYear]);

  useEffect(() => {
    if (currentStep === 3) {
      setMediaFormats([]); // clear stale data so it reloads scoped to the chosen retailer
      void loadMediaFormats();
    }
    if (currentStep === 4) void loadOperators();
    if (currentStep === 7) {
      void loadOperators();
      void loadOperatorTaskTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleDownloadTemplate = () => {
    window.open("/api/campaign-import/template", "_blank");
  };

  const handleImportFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".csv")) {
      toast({
        title: "Unsupported file",
        description: "Use .xlsx or .csv for the import sheet.",
        variant: "destructive",
      });
      return;
    }
    setImportFile(file);
    resetImportRunState();
    if (filenameLooksLikePackBooking(file.name)) {
      toast({
        title: "This looks like a pack booking sheet",
        description: PACK_BOOKING_WIZARD_HINT,
      });
    }
  };

  const runAnalysis = async (options?: { nextStep?: WizardStep; formatMappings?: Record<string, number> }) => {
    if (!importFile) return;
    const requestId = analysisRequestRef.current + 1;
    analysisRequestRef.current = requestId;
    try {
      setIsAnalysing(true);
      const formData = new FormData();
      formData.append("importFile", importFile);
      if (selectedRetailerId) formData.append("retailerId", String(selectedRetailerId));
      if (selectedCycleId) formData.append("cycleId", String(selectedCycleId));
      if (options?.formatMappings && Object.keys(options.formatMappings).length > 0) {
        formData.append("formatMappings", JSON.stringify(options.formatMappings));
      }
      const response = await apiFetch("/api/campaign-import/analyse", {
        method: "POST",
        body: formData,
      });
      const payload = await response.json();
      if (requestId !== analysisRequestRef.current) return;
      if (!response.ok || !payload.success) {
        throw new Error(payload?.error || "Failed to analyse file");
      }
      setAnalysis(payload.analysis);
      const nextStep = options?.nextStep ?? 2;
      setCurrentStep(nextStep);

      const nextDecisions: Record<string, FormatDecision> = {};
      for (const detectedFormat of payload.analysis.detectedFormats ?? []) {
        if (detectedFormat.matchedMediaFormatId) {
          nextDecisions[detectedFormat.importFormatName] = {
            action: "matched",
            mediaFormatId: detectedFormat.matchedMediaFormatId,
          };
        } else if (formatDecisions[detectedFormat.importFormatName]) {
          nextDecisions[detectedFormat.importFormatName] = formatDecisions[detectedFormat.importFormatName];
        } else {
          nextDecisions[detectedFormat.importFormatName] = {
            action: "unresolved",
            mediaFormatId: null,
          };
        }
      }
      setFormatDecisions(nextDecisions);

      toast({
        title: "Import analysed",
        description: `Detected ${payload.analysis.counts.campaignsDetected} campaign groups.`,
      });
    } catch (error: any) {
      if (requestId !== analysisRequestRef.current) return;
      const rawMessage = error?.message || "Could not analyse import file.";
      toast({
        title: "Analysis failed",
        description: adviceForImportAnalyseError(rawMessage) ?? rawMessage,
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      if (requestId === analysisRequestRef.current) {
        setIsAnalysing(false);
      }
    }
  };

  const handleKittingFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx")) {
      toast({
        title: "Unsupported kitting file",
        description: "Kitting reconciliation requires an .xlsx workbook.",
        variant: "destructive",
      });
      return;
    }
    setKittingFile(file);
    setReconciliation(null);
  };

  const buildFormatMappings = (): Record<string, number> => {
    const formatMappings: Record<string, number> = {};
    for (const fmt of analysis?.detectedFormats ?? []) {
      const decision = formatDecisions[fmt.importFormatName];
      if ((decision?.action === "use_existing" || decision?.action === "matched") && decision.mediaFormatId) {
        // User-confirmed or auto-matched — use it directly
        formatMappings[fmt.importFormatName] = decision.mediaFormatId;
      } else if (!decision || decision.action === "unresolved") {
        // useEffect may not have fired yet — apply best fuzzy match as fallback
        const suggestion = bestFuzzyMatch(fmt.importFormatName);
        if (suggestion) {
          formatMappings[fmt.importFormatName] = suggestion.id;
        }
      }
    }
    return formatMappings;
  };

  const createNewMediaFormats = async (): Promise<Record<string, number>> => {
    if (!selectedRetailerId || !analysis) return {};

    const createdFormatMappings: Record<string, number> = {};
    const existingFormatByName = new Map(
      mediaFormats.map((format) => [format.name.trim().toLowerCase(), format]),
    );
    const formatsToCreate = (analysis.detectedFormats ?? []).filter((format) => {
      const decision = formatDecisions[format.importFormatName];
      return decision?.action === "create_new";
    });

    if (formatsToCreate.length === 0) {
      setFormatMappingsToOnboard({});
      return {};
    }

    setIsPersistingFormats(true);
    try {
      for (const format of formatsToCreate) {
        const normalizedName = format.importFormatName.trim().toLowerCase();
        const existingFormat = existingFormatByName.get(normalizedName);

        if (existingFormat) {
          createdFormatMappings[format.importFormatName] = existingFormat.id;
          continue;
        }

        const response = await apiFetch("/api/media-formats", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: format.importFormatName.trim(),
            type: "Imported",
            description: `Created by import wizard from ${importFile?.name ?? "campaign import"}`,
            retailerId: selectedRetailerId,
            quantityType: "fixed",
          }),
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || typeof payload?.id !== "number") {
          throw new Error(
            payload?.detail ||
              payload?.error ||
              `Failed to create media format '${format.importFormatName}'`,
          );
        }

        const createdFormat = {
          id: payload.id as number,
          name: typeof payload.name === "string" ? payload.name : format.importFormatName,
        };
        existingFormatByName.set(createdFormat.name.trim().toLowerCase(), createdFormat);
        createdFormatMappings[format.importFormatName] = createdFormat.id;
      }

      setMediaFormats((prev) => {
        const byId = new Map(prev.map((format) => [format.id, format]));
        for (const [importFormatName, mediaFormatId] of Object.entries(createdFormatMappings)) {
          if (!byId.has(mediaFormatId)) {
            byId.set(mediaFormatId, { id: mediaFormatId, name: importFormatName });
          }
        }
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
      });

      setFormatDecisions((prev) => {
        const next = { ...prev };
        for (const [importFormatName, mediaFormatId] of Object.entries(createdFormatMappings)) {
          next[importFormatName] = {
            action: "use_existing",
            mediaFormatId,
            isSuggested: false,
          };
        }
        return next;
      });
      setFormatMappingsToOnboard(createdFormatMappings);

      toast({
        title: "New formats created",
        description: `Prepared ${Object.keys(createdFormatMappings).length} new format(s) for store/operator setup.`,
      });

      return createdFormatMappings;
    } finally {
      setIsPersistingFormats(false);
    }
  };

  const runReconciliation = async () => {
    if (!importFile || !kittingFile) return;
    const requestId = reconciliationRequestRef.current + 1;
    reconciliationRequestRef.current = requestId;
    try {
      setIsReconciling(true);
      const formatMappings = buildFormatMappings();
      const formData = new FormData();
      formData.append("importFile", importFile);
      formData.append("kittingFile", kittingFile);
      if (selectedRetailerId) formData.append("retailerId", String(selectedRetailerId));
      if (selectedCycleId) formData.append("cycleId", String(selectedCycleId));
      if (Object.keys(formatMappings).length > 0) {
        formData.append("formatMappings", JSON.stringify(formatMappings));
      }

      const response = await apiFetch("/api/campaign-import/reconcile-kitting", {
        method: "POST",
        body: formData,
      });
      const payload = await response.json();
      if (requestId !== reconciliationRequestRef.current) return;
      if (!response.ok || !payload.success) {
        throw new Error(payload?.error || "Reconciliation failed");
      }
      setReconciliation(payload.reconciliation);
      setCurrentStep(7);
      if (payload.reconciliation.blocking) {
        toast({
          title: "Reconciliation blocked",
          description: `${payload.reconciliation.mismatchRows} mismatches found.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Reconciliation passed",
          description: "Import matches the kitting sheet snapshot.",
        });
      }
    } catch (error: any) {
      if (requestId !== reconciliationRequestRef.current) return;
      toast({
        title: "Reconciliation failed",
        description: error?.message || "Could not reconcile files.",
        variant: "destructive",
      });
    } finally {
      if (requestId === reconciliationRequestRef.current) {
        setIsReconciling(false);
      }
    }
  };

  const goNext = async () => {
    if (currentStep === 7) return;
    if (currentStep === 5) {
      setCurrentStep(7);
      return;
    }
    if (currentStep === 6) {
      setCurrentStep(7);
      return;
    }
    if (!canProceedFromStep(currentStep)) {
      toast({
        title: "Step blocked",
        description: currentStepBlockReason ?? "Resolve blockers in the current step before continuing.",
        variant: "destructive",
      });
      return;
    }
    if (currentStep === 3) {
      try {
        const formatMappings = {
          ...buildFormatMappings(),
          ...(await createNewMediaFormats()),
        };
        await runAnalysis({ nextStep: 4, formatMappings });
      } catch (error: any) {
        toast({
          title: "Could not create new formats",
          description: error?.message || "Create the missing formats before continuing.",
          variant: "destructive",
        });
      }
      return;
    }
    setCurrentStep((prev) => (prev + 1) as WizardStep);
  };

  const goBack = () => {
    if (currentStep === 1) return;
    if (currentStep === 7) {
      setCurrentStep(5);
      return;
    }
    setCurrentStep((prev) => (prev - 1) as WizardStep);
  };

  const renderStatusBadge = (campaign: CampaignTile) => {
    const statuses = campaign.statuses ?? [];
    if (statuses.includes("installer_issues")) {
      return <WizardPill tone="danger">Installer Issues</WizardPill>;
    }
    if (statuses.includes("needs_format")) {
      return <WizardPill tone="warning">Needs Format</WizardPill>;
    }
    if (statuses.includes("warnings")) {
      return <WizardPill tone="warning">Warnings</WizardPill>;
    }
    return <WizardPill tone="success">Ready</WizardPill>;
  };

  return (
    <div>
      <div className="bg-white shadow-sm border-b border-gray-100 p-4 lg:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Create Campaign: Import</h2>
            <p className="text-gray-600 mt-1">Template-driven import with validation and kitting reconciliation.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            <Link href="/create-campaign">
              <Button variant="ghost">
                <Target className="h-4 w-4 mr-2" />
                Manual Flow
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2 mb-3">
                {STEP_LABELS.map((step) => (
                  <WizardPill key={step.id} tone={step.id <= currentStep ? "active" : "neutral"}>
                    {step.id}. {step.label}
                  </WizardPill>
                ))}
              </div>
              <Progress value={stepProgress} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{STEP_LABELS[currentStep - 1].label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {currentStep === 1 && (
                <>
                  {/* Year + Retailer + Cycle selection — required before upload */}
                  <div className="rounded-md border bg-amber-50 border-amber-200 px-3 py-2 text-sm text-amber-900">
                    Choose the campaign year first. The cycle list below is filtered to {selectedYear}, so imports cannot
                    silently land in the wrong year's cycle.
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Campaign year</label>
                      <div className="border rounded px-3 py-2 min-h-[42px] flex items-center">
                        <YearFilter showIcon={false} />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Retailer</label>
                      <select
                        className="border rounded px-3 py-2 text-sm w-full"
                        value={selectedRetailerId ?? ""}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (Number.isFinite(val) && val > 0) {
                            setSelectedRetailerId(val);
                          } else {
                            cycleLoadRequestRef.current += 1;
                            setSelectedRetailerId(null);
                            setCycles([]);
                            setSelectedCycleId(null);
                            setIsCyclesLoading(false);
                          }
                          resetImportRunState();
                        }}
                      >
                        <option value="">— Select retailer —</option>
                        {retailers.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Cycle</label>
                      <select
                        className="border rounded px-3 py-2 text-sm w-full"
                        disabled={!selectedRetailerId || isCyclesLoading}
                        value={selectedCycleId ?? ""}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setSelectedCycleId(Number.isFinite(val) && val > 0 ? val : null);
                          resetImportRunState();
                        }}
                      >
                        <option value="">
                          {isCyclesLoading
                            ? `Loading ${selectedYear} cycles...`
                            : !selectedRetailerId
                              ? "Select a retailer first"
                              : cycles.length === 0
                                ? `No ${selectedYear} cycles found`
                                : `— Select ${selectedYear} cycle —`}
                        </option>
                        {cycles.map((c) => (
                          <option key={c.id} value={c.id}>{getCycleOptionLabel(c)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* File upload — only shown once retailer + cycle are chosen */}
                  {selectedRetailerId && selectedCycleId ? (
                    <>
                      <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx,.csv"
                        className="hidden"
                        onChange={handleImportFileSelect}
                      />
                      {!importFile ? (
                        <div
                          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary"
                          onClick={() => importInputRef.current?.click()}
                        >
                          <CloudUpload className="h-10 w-10 mx-auto mb-2 text-gray-500" />
                          <p className="font-medium">Upload Master Install List file</p>
                          <p className="text-sm text-muted-foreground">Supports .xlsx and .csv</p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between border rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            <div>
                              <p className="font-medium">{importFile.name}</p>
                              <p className="text-xs text-muted-foreground">{(importFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <Button onClick={() => void runAnalysis()} disabled={isAnalysing}>
                            {isAnalysing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Analyse Import
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center opacity-40 select-none">
                      <CloudUpload className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                      <p className="font-medium text-gray-500">Select a retailer and cycle above to upload</p>
                    </div>
                  )}
                </>
              )}

              {currentStep === 2 && analysis && (
                <>
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      We detected {analysis.counts.campaignsDetected} campaigns across {analysis.counts.uniqueStores} stores.
                    </AlertDescription>
                  </Alert>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {analysis.campaigns.map((campaign) => (
                      <Card key={campaign.campaignName}>
                        <CardContent className="pt-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium">{campaign.campaignName}</p>
                            {renderStatusBadge(campaign)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Stores: {campaign.detectedStoreCount}
                            {campaign.declaredStoreCount !== null ? ` / ${campaign.declaredStoreCount} declared` : ""}
                          </p>
                          <p className="text-sm text-muted-foreground">Formats detected: {campaign.formatCount}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              {currentStep === 3 && analysis && (
                <>
                  {hasUnresolvedFormatDecision ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Every format must be mapped to an existing system format or marked as new before you can continue.
                      </AlertDescription>
                    </Alert>
                  ) : hasSuggestedFormatDecisions ? (
                    <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        Some formats were auto-matched by similarity. Review the amber rows and confirm or change the mapping.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>All formats confirmed. Ready to continue.</AlertDescription>
                    </Alert>
                  )}

                  {hasCreateNewFormatDecisions ? (
                    <Alert className="border-blue-300 bg-blue-50 text-blue-900">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertDescription>
                        New formats will create permanent media format records plus store/format/operator assignments for the imported stores before campaigns are created. You can delete those formats later if the import is not kept.
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <div className="space-y-3">
                    {(analysis.detectedFormats ?? []).map((format) => {
                      const decision = formatDecisions[format.importFormatName] ?? {
                        action: "unresolved" as const,
                        mediaFormatId: null,
                      };

                      const isExactMatch = decision.action === "matched" || (decision.action === "use_existing" && !decision.isSuggested && Boolean(decision.mediaFormatId));
                      const isSuggested = decision.isSuggested === true;
                      const isCreateNew = decision.action === "create_new";
                      const isUnresolved = decision.action === "unresolved" || (decision.action === "use_existing" && !decision.mediaFormatId);

                      const selectedId =
                        (decision.action === "use_existing" || decision.action === "matched") && decision.mediaFormatId
                          ? String(decision.mediaFormatId)
                          : "";

                      return (
                        <div
                          key={format.importFormatName}
                          className={`border rounded p-3 space-y-2 ${
                            isCreateNew
                              ? "border-blue-300 bg-blue-50/40"
                              : isSuggested
                                ? "border-amber-300 bg-amber-50/40"
                                : isUnresolved
                                  ? "border-red-300 bg-red-50/40"
                                  : "border-green-300 bg-green-50/40"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="font-medium font-mono text-sm">{format.importFormatName}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {format.storesWithFormatAvailable}/{format.storesUsingFormat} stores have this format
                              </span>
                              {isCreateNew ? (
                                <WizardPill tone="info">New format</WizardPill>
                              ) : isSuggested ? (
                                <WizardPill tone="warning">Suggested - verify</WizardPill>
                              ) : isUnresolved ? (
                                <WizardPill tone="danger">Needs action</WizardPill>
                              ) : (
                                <WizardPill tone="success">Confirmed</WizardPill>
                              )}
                            </div>
                          </div>

                          {/* Unified mapping row — always editable */}
                          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                            <div className="flex-1 min-w-0">
                              <select
                                className={`border rounded px-3 py-2 text-sm w-full ${isCreateNew ? "opacity-40 pointer-events-none" : ""}`}
                                value={isCreateNew ? "" : selectedId}
                                disabled={isCreateNew}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  if (Number.isFinite(val) && val > 0) {
                                    setFormatDecisions((prev) => ({
                                      ...prev,
                                      [format.importFormatName]: {
                                        action: "use_existing",
                                        mediaFormatId: val,
                                        isSuggested: false,
                                      },
                                    }));
                                  } else {
                                    setFormatDecisions((prev) => ({
                                      ...prev,
                                      [format.importFormatName]: { action: "unresolved", mediaFormatId: null },
                                    }));
                                  }
                                }}
                              >
                                <option value="">— Select system format —</option>
                                {mediaFormats.map((mf) => (
                                  <option key={mf.id} value={mf.id}>
                                    {mf.name}
                                  </option>
                                ))}
                              </select>
                              {isSuggested && selectedId && (
                                <p className="text-xs text-amber-700 mt-1">
                                  Auto-suggested by name similarity. Confirm this is correct or pick a different format.
                                </p>
                              )}
                            </div>

                            {isExactMatch ? (
                              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
                                Using existing format
                              </div>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant={isCreateNew ? "default" : "outline"}
                                className={isCreateNew ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                                onClick={() => {
                                  if (isCreateNew) {
                                    // Toggle off — revert to unresolved so the select becomes active again
                                    setFormatDecisions((prev) => ({
                                      ...prev,
                                      [format.importFormatName]: { action: "unresolved", mediaFormatId: null },
                                    }));
                                  } else {
                                    setFormatDecisions((prev) => ({
                                      ...prev,
                                      [format.importFormatName]: { action: "create_new", mediaFormatId: null },
                                    }));
                                  }
                                }}
                              >
                                {isCreateNew ? "✓ Creating new — undo?" : "Create as new format"}
                              </Button>
                            )}
                          </div>
                          {isCreateNew ? (
                            <p className="text-xs text-blue-700">
                              Continuing will create this format and set it up for the imported stores/operators. If this import is discarded, the format and assignments can be deleted later.
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-2">
                    {analysis.campaigns.map((campaign) => (
                      <div key={campaign.campaignName} className="border rounded p-3">
                        <p className="font-medium">{campaign.campaignName}</p>
                        <p className="text-sm text-muted-foreground">
                          {(campaign.formats ?? []).length > 0 ? (campaign.formats ?? []).join(", ") : "No formats found"}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {currentStep === 4 && analysis && (
                <div className="space-y-4">
                  {unresolvedInstallers.length === 0 ? (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        All stores have an operator already assigned in the system for each format. No action needed.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      {allOperatorsAssigned ? (
                        <Alert>
                          <CheckCircle2 className="h-4 w-4" />
                          <AlertDescription>
                            All {unresolvedInstallers.length} store/format combinations have been assigned an operator for this import.
                          </AlertDescription>
                        </Alert>
                      ) : step4Acknowledged ? (
                        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          <AlertDescription>
                            Acknowledged — {unresolvedInstallers.filter((item) => {
                              const { storeNo, formatName } = normaliseUnresolved(item);
                              return (operatorOverrides[`${storeNo}|||${formatName}`] ?? null) === null;
                            }).length} combinations still unassigned. You can assign operators in <em>Retailer Management → Bulk Store Assignments</em> before committing.
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>{unresolvedInstallers.length} store/format combinations</strong> have no operator assigned in the system.
                            Select an operator for each row below, or acknowledge and proceed to handle assignments later.
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="rounded border overflow-hidden">
                        <p className="px-3 py-2 text-xs text-muted-foreground border-b bg-gray-50">
                          Operators are auto-selected from active operators in each store (alphabetical). You can change any row below.
                        </p>
                        <table className="w-full text-sm">
                          <thead className="bg-muted text-muted-foreground">
                            <tr>
                              <th className="text-left px-3 py-2">Store</th>
                              <th className="text-left px-3 py-2">Format</th>
                              <th className="text-left px-3 py-2">Rows affected</th>
                              <th className="text-left px-3 py-2 w-[220px]">Assign operator for import</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unresolvedInstallers.map((item) => {
                              const { storeNo, storeName, formatName } = normaliseUnresolved(item);
                              const key = `${storeNo}|||${formatName}`;
                              const selectedId = operatorOverrides[key] ?? null;
                              // Use operators already active in this store; fall back to full list if none found.
                              const storeOperators =
                                item.activeOperators && item.activeOperators.length > 0
                                  ? [...item.activeOperators].sort((a, b) => a.name.localeCompare(b.name))
                                  : operators;
                              const noOperatorsInStore = item.activeOperators?.length === 0;
                              return (
                                <tr key={key} className="border-t">
                                  <td className="px-3 py-2 font-medium">
                                    {storeName || storeNo}
                                    {storeNo && storeName !== storeNo && (
                                      <span className="text-xs text-muted-foreground ml-1">#{storeNo}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-muted-foreground">{formatName || "—"}</td>
                                  <td className="px-3 py-2">
                                    <WizardPill tone="neutral">{item.count}</WizardPill>
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      className="border rounded px-2 py-1 text-sm w-full"
                                      value={selectedId !== null ? String(selectedId) : ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setOperatorOverrides((prev) => ({
                                          ...prev,
                                          [key]: val ? Number(val) : null,
                                        }));
                                      }}
                                    >
                                      <option value="">
                                        {noOperatorsInStore ? "No operators in this store" : "Leave unassigned"}
                                      </option>
                                      {storeOperators.map((op) => (
                                        <option key={op.id} value={op.id}>
                                          {op.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {!allOperatorsAssigned && !step4Acknowledged && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setStep4Acknowledged(true)}
                        >
                          Acknowledge & proceed without assigning all operators now
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}

              {currentStep === 5 && analysis && (
                <div className="space-y-4">
                  {/*
                    Defensive guard: never surface operator-coverage warnings in step 5 UI.
                    Step 4 is the source of truth for operator assignment handling.
                  */}
                  {(() => {
                    const step5Warnings = validationWarnings.filter((msg) => !isOperatorCoverageWarning(msg));
                    return (
                      <>
                  <div>
                    <h4 className="font-semibold mb-2">Blocking issues</h4>
                    {blockingErrors.length === 0 ? (
                      <p className="text-sm text-green-700">No blocking validation issues found.</p>
                    ) : (
                      <div className="space-y-2">
                        {blockingErrors.map((msg, idx) => (
                          <Alert key={idx} variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{msg}</AlertDescription>
                          </Alert>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Warnings</h4>
                    {step5Warnings.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No warnings.</p>
                    ) : (
                      <div className="space-y-2">
                        {step5Warnings.map((msg, idx) => (
                          <Alert key={idx}>
                            <AlertDescription>{msg}</AlertDescription>
                          </Alert>
                        ))}
                      </div>
                    )}
                  </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {currentStep === 6 && (
                <div className="space-y-4 flex flex-col">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      Using the original import from Step 1: <strong>{importFile?.name ?? "No import file selected"}</strong>.
                      You do not need to upload it again.
                    </AlertDescription>
                  </Alert>
                  <input
                    ref={kittingInputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleKittingFileSelect}
                  />
                  <Button variant="outline" onClick={() => kittingInputRef.current?.click()}>
                    <CloudUpload className="h-4 w-4 mr-2" />
                    Upload System-Generated Kitting Sheet
                  </Button>
                  {kittingFile ? (
                    <div className="flex items-center justify-between border rounded-lg p-3">
                      <div>
                        <p className="font-medium">{kittingFile.name}</p>
                        <p className="text-xs text-muted-foreground">Used as reconciliation snapshot baseline</p>
                      </div>
                      <Button onClick={runReconciliation} disabled={isReconciling || !importFile}>
                        {isReconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Run Reconciliation
                      </Button>
                    </div>
                  ) : null}
                  {reconciliation && (
                    <Alert variant={reconciliation.blocking ? "destructive" : "default"}>
                      {reconciliation.blocking ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                      <AlertDescription>
                        Snapshot: {reconciliation.snapshotLabel}. Matched {reconciliation.matchedRows}, mismatched{" "}
                        {reconciliation.mismatchRows}.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {currentStep === 7 && (
                <div className="flex flex-col gap-4">
                  <Alert className="order-1" variant={reconciliation?.blocking ? "destructive" : "default"}>
                    {reconciliation?.blocking ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    <AlertDescription>
                      {reconciliation
                        ? reconciliation.blocking
                          ? "Preview shows differences between uploaded and generated quantities."
                          : "Uploaded and generated quantities match for all compared rows."
                        : "Preview shows uploaded vs generated quantities. Reconciliation is optional."}
                    </AlertDescription>
                  </Alert>
                  <Card className="order-3">
                    <CardHeader className="pb-3">
                      <CardTitle>Create Campaigns</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        One campaign will be created per campaign group. Select an operator task template for each campaign.
                      </p>
                      <div className="overflow-x-auto pb-1">
                        <div className="flex gap-3 min-w-max">
                          {campaignPreviewCards.map((card) => (
                            <div key={card.campaignName} className="w-[340px] border rounded-md p-3 space-y-3 bg-white">
                              <div>
                                <p className="font-medium">{card.campaignName}</p>
                                <p className="text-xs text-muted-foreground">
                                  Groups: {card.groupCount} · Formats: {card.formatNames.length} · Stores: {card.storeNames.length}
                                </p>
                              </div>

                              <div className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Formats</p>
                                <div className="flex flex-wrap gap-1">
                                  {card.formatNames.slice(0, 6).map((formatName) => (
                                    <WizardPill key={`${card.campaignName}-${formatName}`} tone="neutral">
                                      {formatName}
                                    </WizardPill>
                                  ))}
                                  {card.formatNames.length > 6 ? (
                                    <WizardPill tone="info">+{card.formatNames.length - 6} more</WizardPill>
                                  ) : null}
                                </div>
                              </div>

                              <div className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Stores</p>
                                <p className="text-xs text-muted-foreground">
                                  {card.storeNames.slice(0, 3).join(", ")}
                                  {card.storeNames.length > 3 ? ` +${card.storeNames.length - 3} more` : ""}
                                </p>
                              </div>

                              <div className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Operator task</p>
                                <select
                                  className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                                  value={campaignTaskSelections[card.campaignName] ?? ""}
                                  onChange={(e) =>
                                    setCampaignTaskSelections((prev) => ({
                                      ...prev,
                                      [card.campaignName]: e.target.value ? Number(e.target.value) : null,
                                    }))
                                  }
                                >
                                  <option value="">Select operator task…</option>
                                  {operatorTaskTemplates.map((task) => (
                                    <option key={task.id} value={task.id}>
                                      {task.name}{task.taskName ? ` — ${task.taskName}` : ""}
                                    </option>
                                  ))}
                                </select>
                                <p className="text-[11px] text-muted-foreground">
                                  Defaulted to Standard Posting (safe default) when available. You can overwrite this.
                                </p>
                              </div>

                              {(card.missingStoreRows > 0 || card.missingFormatRows > 0) ? (
                                <Alert variant="destructive">
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription>
                                    Store gaps: {card.missingStoreRows}, format gaps: {card.missingFormatRows}
                                  </AlertDescription>
                                </Alert>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Operator assignment is derived from Step 4 store/format selections. Choose operator task per campaign below. Imports always create multiple campaigns — one per campaign group detected in the Master Install List — and they are created sequentially on the server to keep snapshots consistent.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="order-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-3">
                        <CardTitle>Preview Comparison</CardTitle>
                        <Button size="sm" variant="outline" onClick={exportPreviewCsv}>
                          <Download className="h-4 w-4 mr-2" />
                          Export CSV
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 text-sm space-y-3">
                      <p className="text-muted-foreground">
                        Matrix view similar to Compliance Detailed: each format shows uploaded quantity vs generated quantity.
                      </p>
                      <div className="overflow-auto border rounded-md">
                        <table className="w-full min-w-max border-collapse text-sm">
                          <thead>
                            <tr className="bg-slate-100">
                              <th
                                rowSpan={3}
                                className="sticky left-0 z-20 min-w-[240px] border-b border-r px-3 py-2 text-left font-semibold bg-slate-100"
                              >
                                Store
                              </th>
                              {previewMatrix.campaignFormats.map((campaign) => (
                                <th
                                  key={campaign.campaignName}
                                  colSpan={Math.max(1, campaign.formats.length * 2)}
                                  className="border-b border-r px-2 py-2 text-center font-semibold"
                                >
                                  {campaign.campaignName}
                                </th>
                              ))}
                            </tr>
                            <tr className="bg-slate-50">
                              {previewMatrix.campaignFormats.map((campaign) =>
                                campaign.formats.map((formatName) => (
                                  <th
                                    key={`${campaign.campaignName}-${formatName}`}
                                    colSpan={2}
                                    className="border-b border-r px-2 py-2 text-center font-medium"
                                  >
                                    {formatName}
                                  </th>
                                )),
                              )}
                            </tr>
                            <tr className="bg-white">
                              {previewMatrix.campaignFormats.map((campaign) =>
                                campaign.formats.flatMap((formatName) => ([
                                  <th
                                    key={`${campaign.campaignName}-${formatName}-uploaded`}
                                    className="border-b border-r px-2 py-1 text-center font-medium"
                                  >
                                    Uploaded
                                  </th>,
                                  <th
                                    key={`${campaign.campaignName}-${formatName}-generated`}
                                    className="border-b border-r px-2 py-1 text-center font-medium"
                                  >
                                    Generated
                                  </th>,
                                ])),
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {previewMatrix.stores.map((store) => (
                              <tr key={`${store.storeNo}|||${store.storeName}`} className="odd:bg-white even:bg-slate-50/40">
                                <td className="sticky left-0 z-10 border-r border-b bg-inherit px-3 py-2 align-top">
                                  <div className="font-medium">{store.storeName}</div>
                                  <div className="text-xs text-muted-foreground">#{store.storeNo}</div>
                                </td>
                                {previewMatrix.campaignFormats.map((campaign) =>
                                  campaign.formats.flatMap((formatName) => {
                                    const key = normaliseKey(campaign.campaignName, formatName, store.storeName);
                                    const uploadedQty = previewMatrix.uploadedByKey.get(key) ?? 0;
                                    const generatedQty = previewMatrix.generatedByKey.get(key) ?? uploadedQty;
                                    const changed = uploadedQty !== generatedQty;
                                    return [
                                      <td
                                        key={`${store.storeNo}-${campaign.campaignName}-${formatName}-uploaded`}
                                        className="border-r border-b px-2 py-2 text-center tabular-nums"
                                      >
                                        {uploadedQty}
                                      </td>,
                                      <td
                                        key={`${store.storeNo}-${campaign.campaignName}-${formatName}-generated`}
                                        className={`border-r border-b px-2 py-2 text-center tabular-nums ${changed ? "bg-amber-50 text-amber-800 font-medium" : ""}`}
                                      >
                                        {generatedQty}
                                      </td>,
                                    ];
                                  }),
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="order-4 rounded-lg border bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium">Ready to create campaigns?</p>
                        <p className="text-sm text-muted-foreground">
                          This will create {campaignCreationPlan.length} campaign{campaignCreationPlan.length === 1 ? "" : "s"} from the reviewed import.
                        </p>
                      </div>
                      <Button
                        onClick={createImportedCampaigns}
                        disabled={isCreatingCampaigns || campaignCreationPlan.length === 0}
                        className="h-12 px-8 text-base font-semibold sm:min-w-[240px]"
                      >
                        {isCreatingCampaigns ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
                        {isCreatingCampaigns
                          ? "Creating..."
                          : `Create ${campaignCreationPlan.length} ${campaignCreationPlan.length === 1 ? "Campaign" : "Campaigns"}`}
                      </Button>
                    </div>
                  </div>
                  {campaignCreationStatus.length > 0 ? (() => {
                    const total = campaignCreationStatus.length;
                    const successCount = campaignCreationStatus.filter((c) => c.status === "success").length;
                    const failedCount = campaignCreationStatus.filter((c) => c.status === "failed").length;
                    const finishedCount = successCount + failedCount;
                    const creatingIdx = campaignCreationStatus.findIndex((c) => c.status === "creating");
                    const creatingEntry = creatingIdx >= 0 ? campaignCreationStatus[creatingIdx] : null;
                    const progressPct = total === 0 ? 0 : Math.round((finishedCount / total) * 100);

                    const statusIcon = (status: "pending" | "creating" | "success" | "failed") => {
                      if (status === "creating") return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
                      if (status === "success") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
                      if (status === "failed") return <AlertCircle className="h-4 w-4 text-red-600" />;
                      return <Circle className="h-4 w-4 text-slate-400" />;
                    };

                    return (
                      <div className="order-5 rounded-md border bg-white p-3 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm">
                            {isCreatingCampaigns && creatingEntry ? (
                              <span>
                                Creating <span className="font-medium">{creatingIdx + 1}</span> of{" "}
                                <span className="font-medium">{total}</span>:{" "}
                                <span className="font-medium">{creatingEntry.name}</span>
                              </span>
                            ) : isCreatingCampaigns ? (
                              <span>Preparing {total} campaign{total === 1 ? "" : "s"}…</span>
                            ) : failedCount > 0 ? (
                              <span>
                                Created <span className="font-medium text-green-700">{successCount}</span> of{" "}
                                <span className="font-medium">{total}</span> —{" "}
                                <span className="font-medium text-red-700">{failedCount}</span> failed.
                              </span>
                            ) : (
                              <span>
                                Created <span className="font-medium text-green-700">{successCount}</span> of{" "}
                                <span className="font-medium">{total}</span>.
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{progressPct}%</span>
                        </div>
                        <Progress value={progressPct} />
                        <div className="max-h-64 overflow-auto rounded border">
                          <ul className="divide-y text-sm">
                            {campaignCreationStatus.map((entry, i) => (
                              <li
                                key={`${entry.name}-${i}`}
                                className="flex items-start gap-2 px-3 py-2"
                              >
                                <span className="mt-0.5 shrink-0">{statusIcon(entry.status)}</span>
                                <span className="flex-1 min-w-0">
                                  {entry.status === "success" && entry.id ? (
                                    <a
                                      className="text-primary underline"
                                      href={`/campaigns/${entry.id}`}
                                    >
                                      {entry.name}
                                    </a>
                                  ) : (
                                    <span>{entry.name}</span>
                                  )}
                                  {entry.status === "success" && entry.id ? (
                                    <span className="text-muted-foreground"> (#{entry.id})</span>
                                  ) : null}
                                  {entry.status === "failed" && entry.error ? (
                                    <p className="text-xs text-red-600 mt-1 break-words">{entry.error}</p>
                                  ) : null}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        {!isCreatingCampaigns && finishedCount === total ? (
                          <div className="flex flex-col gap-2 rounded-md bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-muted-foreground">
                              Finished. Open Campaigns filtered to {selectedRetailerName} / {selectedCycleName} to review the new campaigns.
                            </p>
                            <Link href={selectedCampaignsUrl}>
                              <Button className="w-full sm:w-auto">
                                View campaigns for this retailer/cycle
                              </Button>
                            </Link>
                          </div>
                        ) : null}
                      </div>
                    );
                  })() : null}
                </div>
              )}
            </CardContent>
          </Card>

          {currentStepBlockReason ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{currentStepBlockReason}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-between">
            <Button variant="outline" onClick={goBack} disabled={currentStep === 1}>
              Back
            </Button>
            {currentStep < 7 ? (
              <Button onClick={goNext} disabled={isStepTransitioning}>
                {isStepTransitioning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {isPersistingFormats ? "Creating formats..." : "Next Step"}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
